import React, { useState, useEffect } from 'react';
import { MapPin, Users, Calendar, Clock, Star, Image as ImageIcon, Edit, Save, X, Heart, UserPlus, UserMinus, Mail, Phone, Globe, Share2, Pencil, Check } from 'lucide-react';
import RatingDisplay from './RatingDisplay';
import { Button } from './button';
import { useAuth } from '../../contexts/AuthContext';
import { apiService } from '../../services/apiService';
import { toast } from 'sonner';

const ScrollableRunCard = ({ 
  runner,
  onClick, 
  showHover = true,
  showLocation = true,
  showRating = true,
  buttonText = "View Profile",
  onButtonClick,
  isExpanded = false,
  onToggleExpand,
  isFullProfile = false,
  onRunnerUpdate
}) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionLoading, setConnectionLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const { user } = useAuth();
  
  // Safety check - if no runner, return null
  if (!runner) {
    return null;
  }
  
  // Check if current user is viewing their own profile
  const isOwnProfile = user && user.id === runner.id;

  const handleClick = () => {
    if (onClick) {
      onClick(runner);
    }
  };

  const handleButtonClick = (e) => {
    e.stopPropagation();
    if (onButtonClick) {
      onButtonClick(runner);
    }
  };

  const handleScroll = (e) => {
    const scrollTop = e.target.scrollTop;
    setIsScrolled(scrollTop > 20);
  };

  // Initialize edit data when entering edit mode
  useEffect(() => {
    if (isEditing && runner) {
      setEditData({
        name: runner.name || '',
        location: runner.location || '',
        bio: runner.bio || '',
        pace_range: runner.pace_range || '',
        running_goals: runner.running_goals || '',
        favorite_routes: runner.favorite_routes || '',
        social_media: runner.social_media || '',
        experience_level: runner.experience_level || '',
        weekly_mileage: runner.weekly_mileage || '',
        favorite_distance: runner.favorite_distance || '',
        personal_records: runner.personal_records || '',
        training_philosophy: runner.training_philosophy || '',
        motivation: runner.motivation || ''
      });
    }
  }, [isEditing, runner]);

  // Handle save edits
  const handleSaveEdits = async () => {
    try {
      const response = await apiService.users.updateProfile(editData);
      if (response) {
        toast.success('Profile updated successfully!');
        setIsEditing(false);
        setEditData({});
        if (onRunnerUpdate) {
          onRunnerUpdate(response);
        }
      }
    } catch (error) {
      toast.error('Failed to update profile');
    }
  };

  return (
    <div
      className={`rounded-3xl overflow-hidden shadow-2xl border border-white/20 relative w-[400px] bg-white/95 backdrop-blur-xl ${
        showHover ? 'hover:scale-105 transition-all duration-300 hover:shadow-3xl' : ''
      } ${onClick ? 'cursor-pointer' : ''}`}
      onClick={handleClick}
    >
      {/* Scrollable Content */}
      <div 
        className={`${isFullProfile ? 'max-h-[80vh] overflow-y-auto scrollbar-hide' : ''}`}
        onScroll={handleScroll}
      >
        {/* Image Section */}
        <div className="relative h-[500px] overflow-hidden">
          {runner.runcard_image ? (
            <img 
              src={runner.runcard_image} 
              alt={runner.name}
              className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
              style={{
                objectPosition: 'center center'
              }}
              onError={(e) => {
                console.error('Failed to load run card image:', runner.runcard_image);
                e.target.style.display = 'none';
                e.target.nextElementSibling.style.display = 'flex';
              }}
            />
          ) : null}
          <div className={`w-full h-full ${runner.runcard_image ? 'spray-paint-placeholder' : 'spray-paint-placeholder'}`} style={{display: runner.runcard_image ? 'none' : 'flex'}}>
          </div>
          
          {/* Gradient overlay for better text readability */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
          
          {/* Runner Type Badge - Top Left */}
          <div className="absolute top-4 left-4">
            <span className="bg-black/60 backdrop-blur-sm text-white px-2 py-1 rounded-full text-xs font-medium border border-white/20">
              Runner
            </span>
          </div>

        </div>

        {/* Information Section */}
        <div className="bg-gradient-to-b from-white to-gray-50/50">
        {/* Basic Info Section */}
        <div className="p-4 border-b border-gray-200/50">
          {/* Runner Name */}
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-xl font-bold text-black flex-1" style={{fontFamily: 'Montserrat, sans-serif'}}>
              {runner.name}
            </h3>
            {isOwnProfile && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (isEditing) {
                    handleSaveEdits();
                  } else {
                    setIsEditing(true);
                  }
                }}
                className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors border border-gray-200"
                aria-label={isEditing ? "Save changes" : "Edit runcard"}
              >
                {isEditing ? (
                  <Check className="w-4 h-4 text-gray-700" />
                ) : (
                  <Pencil className="w-4 h-4 text-gray-700" />
                )}
              </button>
            )}
          </div>
          
          {/* Runner Bio */}
          <p className="text-gray-700 text-sm mb-2 leading-relaxed" style={{fontFamily: 'Montserrat, sans-serif'}}>
            {runner.bio || 'Passionate runner who loves hitting the pavement and exploring new routes. Always looking for new challenges and running buddies to share the journey with.'}
          </p>
          
          {/* Location and Rating */}
          <div className="flex flex-col space-y-1 mb-2">
            {showLocation && runner.location && (
              <div className="flex items-center text-gray-600">
                <MapPin className="w-4 h-4 mr-1" />
                <span className="text-sm" style={{fontFamily: 'Montserrat, sans-serif'}}>
                  {runner.location}
                </span>
              </div>
            )}
            {showRating && runner.rating !== undefined && (
              <div className="flex items-center text-gray-600">
                <RatingDisplay 
                  rating={runner.rating} 
                  totalRatings={runner.total_ratings || 0}
                  size="sm"
                  showCount={false}
                  className="text-gray-600"
                />
              </div>
            )}
          </div>
          
          {/* Action buttons */}
          <div className="flex justify-center gap-2">
            {onButtonClick && !isFullProfile && (
              <button 
                className="w-24 bg-black text-white px-3 py-1.5 rounded-full font-semibold text-xs hover:bg-gray-800 transition-colors shadow-lg focus:outline-none focus:ring-2 focus:ring-gray-500" 
                style={{fontFamily: 'Montserrat, sans-serif'}}
                onClick={handleButtonClick}
              >
                {buttonText}
              </button>
            )}
          </div>
        </div>

        {/* Running Stats Section */}
        <div className="p-4 border-b border-gray-200/50 bg-gradient-to-r from-blue-50/30 to-transparent">
          <h4 className="text-base font-bold text-gray-800 mb-3" style={{fontFamily: 'Montserrat, sans-serif'}}>
            Running Stats
          </h4>
          
          {isEditing && isOwnProfile ? (
            <div className="space-y-2">
              <div>
                <label className="text-xs text-gray-600 block mb-1">Pace Range</label>
                <input
                  type="text"
                  value={editData.pace_range || ''}
                  onChange={(e) => setEditData({...editData, pace_range: e.target.value})}
                  className="w-full text-xs p-1 border border-gray-300 rounded"
                  placeholder="7:00-8:00 min/mile"
                />
              </div>
              <div>
                <label className="text-xs text-gray-600 block mb-1">Weekly Mileage</label>
                <input
                  type="text"
                  value={editData.weekly_mileage || ''}
                  onChange={(e) => setEditData({...editData, weekly_mileage: e.target.value})}
                  className="w-full text-xs p-1 border border-gray-300 rounded"
                  placeholder="25-30 miles"
                />
              </div>
              <div>
                <label className="text-xs text-gray-600 block mb-1">Experience Level</label>
                <input
                  type="text"
                  value={editData.experience_level || ''}
                  onChange={(e) => setEditData({...editData, experience_level: e.target.value})}
                  className="w-full text-xs p-1 border border-gray-300 rounded"
                  placeholder="Intermediate"
                />
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {runner.pace_range && (
                <div className="flex items-center gap-3 p-2 bg-white/60 backdrop-blur-sm rounded-lg border border-gray-200/50">
                  <Clock className="w-4 h-4 text-gray-600" />
                  <div>
                    <p className="text-sm font-semibold text-gray-800" style={{fontFamily: 'Montserrat, sans-serif'}}>
                      Pace Range
                    </p>
                    <p className="text-sm text-gray-600" style={{fontFamily: 'Montserrat, sans-serif'}}>
                      {runner.pace_range}
                    </p>
                  </div>
                </div>
              )}
              {runner.weekly_mileage && (
                <div className="flex items-center gap-3 p-2 bg-white/60 backdrop-blur-sm rounded-lg border border-gray-200/50">
                  <Users className="w-4 h-4 text-gray-600" />
                  <div>
                    <p className="text-sm font-semibold text-gray-800" style={{fontFamily: 'Montserrat, sans-serif'}}>
                      Weekly Mileage
                    </p>
                    <p className="text-sm text-gray-600" style={{fontFamily: 'Montserrat, sans-serif'}}>
                      {runner.weekly_mileage}
                    </p>
                  </div>
                </div>
              )}
              {runner.experience_level && (
                <div className="flex items-center gap-3 p-2 bg-white/60 backdrop-blur-sm rounded-lg border border-gray-200/50">
                  <Star className="w-4 h-4 text-gray-600" />
                  <div>
                    <p className="text-sm font-semibold text-gray-800" style={{fontFamily: 'Montserrat, sans-serif'}}>
                      Experience Level
                    </p>
                    <p className="text-sm text-gray-600" style={{fontFamily: 'Montserrat, sans-serif'}}>
                      {runner.experience_level}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Running Goals & Motivation */}
        <div className="p-4 border-b border-gray-200/50 bg-gradient-to-r from-green-50/30 to-transparent">
          <h4 className="text-base font-bold text-gray-800 mb-3" style={{fontFamily: 'Montserrat, sans-serif'}}>
            Goals & Motivation
          </h4>
          
          {isEditing && isOwnProfile ? (
            <div className="space-y-2">
              <div>
                <label className="text-xs text-gray-600 block mb-1">Running Goals</label>
                <textarea
                  value={editData.running_goals || ''}
                  onChange={(e) => setEditData({...editData, running_goals: e.target.value})}
                  className="w-full text-xs p-1 border border-gray-300 rounded"
                  placeholder="Complete a marathon, improve 5K time..."
                  rows={2}
                />
              </div>
              <div>
                <label className="text-xs text-gray-600 block mb-1">Motivation</label>
                <textarea
                  value={editData.motivation || ''}
                  onChange={(e) => setEditData({...editData, motivation: e.target.value})}
                  className="w-full text-xs p-1 border border-gray-300 rounded"
                  placeholder="What keeps you running?"
                  rows={2}
                />
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {runner.running_goals && (
                <div className="flex items-center gap-3 p-2 bg-white/60 backdrop-blur-sm rounded-lg border border-gray-200/50">
                  <Star className="w-4 h-4 text-gray-600" />
                  <div>
                    <p className="text-sm font-semibold text-gray-800" style={{fontFamily: 'Montserrat, sans-serif'}}>
                      Goals
                    </p>
                    <p className="text-sm text-gray-600" style={{fontFamily: 'Montserrat, sans-serif'}}>
                      {runner.running_goals}
                    </p>
                  </div>
                </div>
              )}
              {runner.motivation && (
                <div className="flex items-center gap-3 p-2 bg-white/60 backdrop-blur-sm rounded-lg border border-gray-200/50">
                  <Heart className="w-4 h-4 text-gray-600" />
                  <div>
                    <p className="text-sm font-semibold text-gray-800" style={{fontFamily: 'Montserrat, sans-serif'}}>
                      Motivation
                    </p>
                    <p className="text-sm text-gray-600" style={{fontFamily: 'Montserrat, sans-serif'}}>
                      {runner.motivation}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Personal Records & Achievements */}
        {(runner.personal_records || runner.favorite_distance) && (
          <div className="p-4 border-b border-gray-200/50 bg-gradient-to-r from-purple-50/30 to-transparent">
            <h4 className="text-base font-bold text-gray-800 mb-3" style={{fontFamily: 'Montserrat, sans-serif'}}>
              Personal Records
            </h4>
            
            {isEditing && isOwnProfile ? (
              <div className="space-y-2">
                <div>
                  <label className="text-xs text-gray-600 block mb-1">Personal Records</label>
                  <textarea
                    value={editData.personal_records || ''}
                    onChange={(e) => setEditData({...editData, personal_records: e.target.value})}
                    className="w-full text-xs p-1 border border-gray-300 rounded"
                    placeholder="5K: 18:30, 10K: 38:45, Half: 1:25:30..."
                    rows={2}
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-600 block mb-1">Favorite Distance</label>
                  <input
                    type="text"
                    value={editData.favorite_distance || ''}
                    onChange={(e) => setEditData({...editData, favorite_distance: e.target.value})}
                    className="w-full text-xs p-1 border border-gray-300 rounded"
                    placeholder="Half Marathon"
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                {runner.personal_records && (
                  <div className="flex items-center gap-3 p-2 bg-white/60 backdrop-blur-sm rounded-lg border border-gray-200/50">
                    <Star className="w-4 h-4 text-gray-600" />
                    <div>
                      <p className="text-sm font-semibold text-gray-800" style={{fontFamily: 'Montserrat, sans-serif'}}>
                        Personal Records
                      </p>
                      <p className="text-sm text-gray-600" style={{fontFamily: 'Montserrat, sans-serif'}}>
                        {runner.personal_records}
                      </p>
                    </div>
                  </div>
                )}
                {runner.favorite_distance && (
                  <div className="flex items-center gap-3 p-2 bg-white/60 backdrop-blur-sm rounded-lg border border-gray-200/50">
                    <Calendar className="w-4 h-4 text-gray-600" />
                    <div>
                      <p className="text-sm font-semibold text-gray-800" style={{fontFamily: 'Montserrat, sans-serif'}}>
                        Favorite Distance
                      </p>
                      <p className="text-sm text-gray-600" style={{fontFamily: 'Montserrat, sans-serif'}}>
                        {runner.favorite_distance}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Training Philosophy */}
        {runner.training_philosophy && (
          <div className="p-4 border-b border-gray-200/50 bg-gradient-to-r from-orange-50/30 to-transparent">
            <h4 className="text-base font-bold text-gray-800 mb-3" style={{fontFamily: 'Montserrat, sans-serif'}}>
              Training Philosophy
            </h4>
            
            {isEditing && isOwnProfile ? (
              <textarea
                value={editData.training_philosophy || ''}
                onChange={(e) => setEditData({...editData, training_philosophy: e.target.value})}
                className="w-full text-xs p-1 border border-gray-300 rounded"
                placeholder="Describe your approach to training..."
                rows={3}
              />
            ) : (
              <p className="text-sm text-gray-600 leading-relaxed" style={{fontFamily: 'Montserrat, sans-serif'}}>
                {runner.training_philosophy}
              </p>
            )}
          </div>
        )}

        {/* Favorite Routes */}
        {runner.favorite_routes && (
          <div className="p-4 border-b border-gray-200/50 bg-gradient-to-r from-teal-50/30 to-transparent">
            <h4 className="text-base font-bold text-gray-800 mb-3" style={{fontFamily: 'Montserrat, sans-serif'}}>
              Favorite Routes
            </h4>
            
            {isEditing && isOwnProfile ? (
              <textarea
                value={editData.favorite_routes || ''}
                onChange={(e) => setEditData({...editData, favorite_routes: e.target.value})}
                className="w-full text-xs p-1 border border-gray-300 rounded"
                placeholder="Describe your favorite running routes..."
                rows={3}
              />
            ) : (
              <p className="text-sm text-gray-600 leading-relaxed" style={{fontFamily: 'Montserrat, sans-serif'}}>
                {runner.favorite_routes}
              </p>
            )}
          </div>
        )}

        {/* Contact Information */}
        <div className="p-4 border-b border-gray-200/50 bg-gradient-to-r from-indigo-50/30 to-transparent">
          <h4 className="text-sm font-bold text-gray-800 mb-3" style={{fontFamily: 'Montserrat, sans-serif'}}>
            Contact
          </h4>
          
          {isEditing && isOwnProfile ? (
            <div className="space-y-2">
              <div>
                <label className="text-xs text-gray-600 block mb-1">Social Media</label>
                <input
                  type="text"
                  value={editData.social_media || ''}
                  onChange={(e) => setEditData({...editData, social_media: e.target.value})}
                  className="w-full text-xs p-1 border border-gray-300 rounded"
                  placeholder="@username"
                />
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {runner.social_media && (
                <div className="flex items-center gap-3 p-2 bg-white/60 backdrop-blur-sm rounded-lg border border-gray-200/50">
                  <Share2 className="w-4 h-4 text-gray-600" />
                  <p className="text-sm text-gray-700 font-medium" style={{fontFamily: 'Montserrat, sans-serif'}}>
                    {runner.social_media}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Save/Cancel Buttons for Edit Mode */}
        {isEditing && isOwnProfile && (
          <div className="p-4 border-b border-gray-200/50 bg-gradient-to-r from-green-50/30 to-transparent">
            <div className="flex gap-3 justify-center">
              <Button
                onClick={handleSaveEdits}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-full font-semibold text-xs transition-colors shadow-lg flex items-center gap-2"
                style={{fontFamily: 'Montserrat, sans-serif'}}
              >
                <Save className="w-3 h-3" />
                Save Changes
              </Button>
              <Button
                onClick={() => {
                  setIsEditing(false);
                  setEditData({});
                }}
                className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-full font-semibold text-xs transition-colors shadow-lg flex items-center gap-2"
                style={{fontFamily: 'Montserrat, sans-serif'}}
              >
                <X className="w-3 h-3" />
                Cancel
              </Button>
            </div>
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default ScrollableRunCard;
